import React, { FC, useMemo } from 'react'
import HTMLReactParser from 'html-react-parser'
import { Headline } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { CardGreyHeadline, CardOutlined, CardOutlinedInner } from 'components/Card'
import { parseDateToDeadline, parseFrozenDeliveryType, parseTimeToDeadline, returnCodeToBr } from 'lib/helper'
import { StoreDeliveryBreadType } from 'types/storeDeliveryBread'
import { useQueryDeliveryCompanies } from '../../../hooks/masterData'

type Props = {
  storeDeliveryBread: StoreDeliveryBreadType
}

const DeliveryBreadSettings: FC<Props> = React.memo((props) => {
  const { storeDeliveryBread } = props
  const {
    maximum_order,
    package_size,
    choices,
    delivery_company,
    frozen_delivery_type,
    is_frozen_delivery,
    how_to_store,
    how_to_eat,
    hours_to_deadline,
  } = storeDeliveryBread
  const deliveryCompanies = useQueryDeliveryCompanies()
  const deliveryCompanyName = useMemo(
    () => deliveryCompanies.find((_) => _.code === delivery_company)?.displayName || '',
    [delivery_company, deliveryCompanies]
  )
  const deadlineDate = parseDateToDeadline(hours_to_deadline)
  const deadlineTime = parseTimeToDeadline(hours_to_deadline)
  return (
    <>
      <Headline headline={'取り寄せの設定'} />
      <Spacer size={20} />
      <CardOutlined>
        <CardGreyHeadline headline={'1日に注文を受け付ける販売上限数'} isTop={true} />
        <Spacer size={8} />
        <CardOutlinedInner>{maximum_order}個</CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'配送サイズ'} />
        <Spacer size={8} />
        <CardOutlinedInner>{package_size}サイズ</CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'配送会社'} />
        <Spacer size={8} />
        <CardOutlinedInner>{deliveryCompanyName}</CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'クール便の有無'} />
        <Spacer size={8} />
        <CardOutlinedInner>{is_frozen_delivery ? 'クール便を利用する' : 'クール便を利用しない'}</CardOutlinedInner>
        <Spacer size={8} />
        {is_frozen_delivery && (
          <>
            <CardOutlinedInner>{parseFrozenDeliveryType(frozen_delivery_type)}</CardOutlinedInner>
            <Spacer size={8} />
          </>
        )}
        <CardGreyHeadline headline={'注文締め切り'} />
        <Spacer size={8} />
        <CardOutlinedInner>
          {deadlineDate === '0' ? '当日' : `${deadlineDate}日前`}&nbsp;{deadlineTime}
        </CardOutlinedInner>
        <Spacer size={8} />
        {how_to_eat !== '' && (
          <>
            <CardGreyHeadline headline={'お召し上がり方'} />
            <Spacer size={8} />
            <CardOutlinedInner>{HTMLReactParser(returnCodeToBr(how_to_eat))}</CardOutlinedInner>
            <Spacer size={8} />
          </>
        )}
        {how_to_store !== '' && (
          <>
            <CardGreyHeadline headline={'保存方法'} />
            <Spacer size={8} />
            <CardOutlinedInner>{HTMLReactParser(returnCodeToBr(how_to_store))}</CardOutlinedInner>
            <Spacer size={8} />
          </>
        )}
        {choices.length > 0 && (
          <>
            <CardGreyHeadline headline={'商品の受渡しオプション'} />
            <Spacer size={8} />
            <CardOutlinedInner>
              {choices.map((choice, index) => (
                <React.Fragment key={choice}>
                  <span>{choice}</span>
                  {index + 1 !== choices.length && <br />}
                </React.Fragment>
              ))}
            </CardOutlinedInner>
            <Spacer size={8} />
          </>
        )}
      </CardOutlined>
    </>
  )
})

export default DeliveryBreadSettings
